import { FileWriter } from '@/platform'
import DataMap, { mapMidForMapId, measureDataMapBinByteLength } from '@/model/DataMap'
import FilePos from '@/model/FilePos'
import ModuleId from '@/model/DataStructModuleId'
import type MainProject from '@/model/MainProject'
import type MapInfo from '@/model/MapInfo'
import type StoryTree from '@/model/StoryTree'

export type ProjectBinWriteStep = {
	mid: number
	write: (writer: FileWriter) => void
}

function appendMapWriteSteps(
	storyTree: StoryTree,
	steps: ProjectBinWriteStep[],
	maps: MapInfo[] = storyTree.rootMaps,
): void {
	for (const map of maps) {
		const mapId = map.Id
		steps.push({
			mid: mapMidForMapId(mapId),
			write: (writer) => {
				map.List = storyTree.loadEvent(mapId)
				map.writeBin(writer)
			},
		})
		appendMapWriteSteps(storyTree, steps, map.Childs)
	}
}

/**
 * 构建 projects.bin 模块写入顺序（与 DataMap.map 顺序一致）。
 * StoryTree 元数据后紧跟各 MapInfo，供现有顺序 readBin 读取。
 */
export function buildProjectBinWriteSteps(project: MainProject): ProjectBinWriteStep[] {
	const steps: ProjectBinWriteStep[] = [
		{ mid: ModuleId.projectHeader, write: (w) => project.projectHeader.writeBin(w) },
		{ mid: ModuleId.screenHash, write: (w) => project.screenHash.writeBin(w) },
		{ mid: ModuleId.projectConstant, write: (w) => project.projectConstant.writeBin(w) },
		{ mid: ModuleId.systemData, write: (w) => project.systemData.writeBin(w) },
		{ mid: ModuleId.gameVar, write: (w) => project.gameVar.writeBin(w) },
		{ mid: ModuleId.gameVarEx, write: (w) => project.gameVarEx.writeBin(w) },
		{ mid: ModuleId.gameStr, write: (w) => project.gameStr.writeBin(w) },
	]
	if (project.dataVer >= 110) {
		steps.push(
			{ mid: ModuleId.gameArray, write: (w) => project.gameArray.writeBin(w) },
			{ mid: ModuleId.gameObj, write: (w) => project.gameObj.writeBin(w) },
		)
	}
	steps.push(
		{ mid: ModuleId.gameUnite, write: (w) => project.gameUnite.writeBin(w) },
		{ mid: ModuleId.userRm, write: (w) => project.userRm.writeBin(w) },
		{ mid: ModuleId.storyTree, write: (w) => project.storyTree.writeBinHeader(w) },
	)
	appendMapWriteSteps(project.storyTree, steps)
	return steps
}

/**
 * 写入完整 projects.bin：DataMap 占位 → 顺序写模块并记账 → 回填 DataMap。
 * 调用前须已 syncDataVersion。
 */
export function writeProjectBinBuffer(project: MainProject): Uint8Array {
	const steps = buildProjectBinWriteSteps(project)
	const dataMapByteLength = measureDataMapBinByteLength(steps.length)
	const writer = new FileWriter()

	writer.writeString(project.magic)
	writer.writeInt(project.dataVer)
	const dataMapStart = writer.length // 记录 DataMap 起始位置
	writer.writeBytes(new Uint8Array(dataMapByteLength)) // 写入 DataMap 占位

	const filePosByMid = new Map<number, FilePos>() // 记录各模块起始位置
	for (const step of steps) {
		const startPos = writer.length
		step.write(writer)
		const fp = new FilePos(step.mid)
		fp.startPos = startPos
		fp.dataLength = writer.length - startPos
		filePosByMid.set(step.mid, fp)
	}
	writer.writeInt(project.crc32)

	const dataMap = new DataMap()
	dataMap.ms = 'RESMAP'
	dataMap.pos.fmid = 0
	dataMap.pos.startPos = dataMapStart
	dataMap.pos.dataLength = dataMapByteLength
	dataMap.map = steps.map((step) => {
		const fp = filePosByMid.get(step.mid)
		if (!fp) {
			throw new Error(`[projectBinWrite] DataMap 缺少模块 mid=${step.mid}`)
		}
		return fp
	})
	project.dataMap = dataMap

	const bytes = writer.toUint8Array()
	const mapWriter = new FileWriter()
	dataMap.writeBin(mapWriter)
	const mapBytes = mapWriter.toUint8Array()
	if (mapBytes.length !== dataMapByteLength) {
		throw new Error(
			`[projectBinWrite] DataMap 占位 ${dataMapByteLength} 字节与真实 ${mapBytes.length} 字节不一致`,
		)
	}
	bytes.set(mapBytes, dataMapStart)
	return bytes
}
